import type { HTMLAttributes, ReactElement, ReactNode } from "react";
import type { ButtonShape } from "../atoms/Button/button-variants.js";
import { SHAPE_CLASS } from "../lib/shape.js";
import { FLOATING_SURFACE, GLASS_SURFACE } from "../lib/surfaces.js";
import { cn } from "../lib/utils.js";

/* A bar of controls floating over a canvas (floating-toolbar, plan v8 wave
   6). Data-blind: the caller places it and fills it; the part owns the
   layout, the paint and the corner.

   The paint comes from lib/surfaces.ts (design-owner decision 7: the two
   floating paints stay) and the corner from lib/shape.ts (decision 4: a
   floating bar's corner is a named shape option; the default is the pill).
   The part writes no fill, border, blur or shadow class of its own. The
   layout is fixed. `className` places the bar (position, offsets, z-index,
   pointer-events): it comes first in cn(), so it cannot change the padding,
   gap, corner or paint.

   It is a named `role="group"` and never a `role="toolbar"`, the same choice
   WorkspaceControlGroup records: a toolbar would oblige the APG roving
   tabindex contract, and these bars are plain Tab order today. Separate
   groups of controls with `ToolbarDivider`. */

/** Which way the controls run. */
export type FloatingToolbarOrientation = "horizontal" | "vertical";

/** The floating paint (decision 7). `default` is the workspace bar paint;
 *  `glass` the see-through paint over a canvas (today's VSM selection bar). */
export type FloatingToolbarSurface = "default" | "glass";

/** The bar's corner, a C7 shape word. `pill` (default) is fully round;
 *  `rounded` is 8px, the same corner as Button shape="rounded"; `panel` is
 *  10px, the base radius (decision 9). */
export type FloatingToolbarShape = Exclude<ButtonShape, "circle"> | "panel";

export interface FloatingToolbarProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "role" | "aria-label" | "className" | "children"
> {
  /** Accessible name for the group. Required: an unnamed row of controls is
   *  announced as an anonymous blob. */
  readonly label: string;
  /** Default `horizontal`. */
  readonly orientation?: FloatingToolbarOrientation | undefined;
  /** Default `default`: the workspace bar paint. */
  readonly surface?: FloatingToolbarSurface | undefined;
  /** Default `pill` (decision 4). */
  readonly shape?: FloatingToolbarShape | undefined;
  /** Placement only: position, offsets, z-index, pointer-events. */
  readonly className?: string | undefined;
  readonly children: ReactNode;
}

const LAYOUT: Record<FloatingToolbarOrientation, string> = {
  horizontal: "flex gap-1 p-1",
  vertical: "flex flex-col items-center gap-1 p-1",
};

const SURFACE: Record<FloatingToolbarSurface, string> = {
  default: FLOATING_SURFACE,
  glass: GLASS_SURFACE,
};

export function FloatingToolbar({
  label,
  orientation = "horizontal",
  surface = "default",
  shape = "pill",
  className,
  children,
  ...rest
}: FloatingToolbarProps): ReactElement {
  return (
    <div
      {...rest}
      role="group"
      aria-label={label}
      className={cn(
        className,
        LAYOUT[orientation],
        SURFACE[surface],
        SHAPE_CLASS[shape],
      )}
    >
      {children}
    </div>
  );
}
